import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { reduxForm } from "redux-form/immutable";
import {
	createLodSpecification,
	createOrder,
	getInputRequirements,
	getPresignedPostUploadUrl,
	setTempAdditionalInfo,
	updateJob,
	uploadToDigitalOcean,
} from "../../../../actions";
import localize, { LocKeys } from "../../../../constants/localizations";
import CustomStepNavigation from "../../../../shared/components/CustomStepper/CustomStepNavigation";
import BodyLayout from "../../../layout/content/BodyLayout";
import FooterLayout from "../../../layout/content/FooterLayout";
import HeaderLayout from "../../../layout/content/HeaderLayout";
import AdditionalInfoEditor from "../../single-view/parts/AdditionalInfoEditor";
import TermsEditor from "../../../administration/terms-and-condition/TermsEditor";
import OrderTotal from "../../../../shared/components/specification/OrderTotal";
import { useNavigate } from "react-router-dom-v5-compat";
import { uploadFile } from "../../../../shared/components/UploadFile/uploadFile";
import { isBimify } from "../../../../utils/permission/user";
import { tokenParams } from "../../../../utils/local-storage.util";

/**
 * Step 4 - Create offer (last step) — review 2026-06-09 §6:
 *   - "Input requirements" (Bimify-authored legal doc, what files to send) is
 *     rendered read-only for everyone; hidden while the BE doc does not exist.
 *   - The Additional information Lexical editor is Bimify-internal: customers
 *     and resellers no longer see or edit it.
 * @param {*} props
 * @returns
 */
const AdditionalInformation = (props) => {
	const navigate = useNavigate();
	const {
		tempBuilding,
		isNaviate = false,
		customer,
		additionalInfo,
		inputRequirementsDetails,
		totalOfferPrice,
		price,
		//Actions
		createOrder,
		setTempAdditionalInfo,
		createLodSpecification,
		cancelButtonAction,
		uploadToDigitalOcean,
		getPresignedPostUploadUrl,
		getInputRequirements,
		updateJob,
	} = props;
	const [isValid, setIsValid] = useState(false);
	const [loading, setLoading] = useState(false);
	const countFile = React.useRef(0);
	const isBimifyUser = isBimify();
	const shouldEstimatePrice = tokenParams()?.company?.shouldEstimatePrice;

	// "Input requirements" document — fetched here (not in the wizard shell) so
	// the Naviate integration flow gets it too, and fetched for ALL users: the
	// section is customer/reseller-facing only, but the details string is still
	// snapshotted onto the order body on submit. Fails silently when no default
	// entry exists yet (the section simply stays hidden).
	useEffect(() => {
		getInputRequirements();
	}, [getInputRequirements]);

	const setValidity = useCallback(() => {
		if (!customer) {
			setIsValid(false);
		} else {
			setIsValid(true);
		}
	}, [customer]);

	useEffect(() => {
		setValidity();
	}, [setValidity]);

	const setNewDescriptionValue = useCallback(
		(newEditorState) => {
			setTempAdditionalInfo(newEditorState);
		},
		[setTempAdditionalInfo]
	);
	//offer, disciplineLevels = [], files = [], cb
	const uploadFiles = useCallback(
		(buildingId, disciplineLevels, files = [], cb) => {
			countFile.current = 0;

			const filesData = [];
			const deliveredToBuildingFileIds = [];

			if (files.length === 0) {
				cb();
			}

			for (const fileItem of files) {
				const { file, id } = fileItem;

				const disciplineLevel = disciplineLevels.find((disciplineLevel) => {
					const findLevel = disciplineLevel.levels.find(
						(level) => level.tempFileId === id
					);

					if (findLevel) {
						return disciplineLevel;
					}

					return null;
				});

				const level = disciplineLevel?.levels.find(
					(level) => level.tempFileId === id
				);

				const dbFileData = {
					file: {
						name: file.name,
						size: file.size,
					},
					path: null,
					buildingId,
					disciplineId: disciplineLevel ? disciplineLevel?.discipline : 1,
					uploadFromOffer: true,
				};
				const preSignedPostUploadUrlBody = {
					fileName: file.name,
					buildingId,
					disciplineId: disciplineLevel ? disciplineLevel?.discipline : 1,
				};

				//------------------
				uploadFile({
					dbFileData,
					file: file,
					preSignedPostUploadUrlBody,
					uploadToDigitalOcean,
					getPresignedPostUploadUrl,
					cbDBUpload: (result) => {
						countFile.current++;

						if (!!disciplineLevel?.discipline) {
							filesData.push({
								id: id,
								file: result.id,
								level: level?.level,
								discipline: disciplineLevel?.discipline,
							});
						} else {
							deliveredToBuildingFileIds.push({
								fileId: +result.id,
								disciplineId: null,
							});
						}

						if (countFile.current === files.length) {
							cb(filesData, deliveredToBuildingFileIds);
						}
					},
				});
			}
		},
		[uploadToDigitalOcean, getPresignedPostUploadUrl]
	);

	const updateJobFile = useCallback(
		(jobId, levels = [], filesData, deliveredToBuildingFileIds, cb) => {
			const disciplineBuildingLevelFiles = [];

			levels &&
				levels.forEach((level) => {
					const findFile =
						filesData && filesData.find((file) => +file.level === +level.level);
					const file = findFile ? findFile?.file || null : null;
					const discipline = findFile ? findFile?.discipline || 1 : 1;

					disciplineBuildingLevelFiles.push({
						level: level.id,
						file: file || null,
						discipline: discipline,
					});
				});

			const body = {
				disciplineBuildingLevelFiles: disciplineBuildingLevelFiles,
			};

			if (jobId) {
				updateJob({
					jobId,
					isNaviate,
					data: body,
					cb: () => {
						cb();
						navigate("/offers");
					},
				});
			}
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[updateJob, isNaviate]
	);

	const createOrderAction = useCallback(
		(projectTemp, newSpecificationId = null) => {
			const priceJS = price ? price?.toJS() : null;
			let levels = [];
			let levelFrom = null;
			let levelTo = null;
			let defaultLevelHeight = null;

			if (isNaviate) {
				const building = tempBuilding && tempBuilding?.toJS();

				levelFrom = building?.levelFrom;
				levelTo = building?.levelTo;
				defaultLevelHeight = building?.defaultLevelHeight;

				building &&
					building.floors.forEach((floor) => {
						levels.push({
							id: floor.id,
							isRoof: floor?.isRoof || false,
							name: floor.name,
							elevation: floor.elevation,
							thickness: floor?.thickness || 0,
							height: floor.height,
							level: floor.level,
						});
					});
			}

			//Buildings
			const tempBuildings = projectTemp?.buildings;
			const pricePerBuilding = priceJS
				? priceJS.buildingPrice?.pricePerBuildingDetailed
				: null;

			let buildings = [];
			let size = 0;
			tempBuildings.forEach((building) => {
				size += +building.size;

				buildings.push({
					price: pricePerBuilding
						? pricePerBuilding?.[building.id]?.buildingPrice
						: null,
					pricePerSquare: pricePerBuilding
						? pricePerBuilding?.[building.id]?.pricePerSquare
						: null,
					type: building.type,
					levelsTotal: !!building.levels ? building.levels : null,
					isCustomName: building.isCustomName,
					uniqueLevels: !!building.uniqueLevels ? building.uniqueLevels : null,
					inputFormat: building.inputFormat,
					size: building.size,
					lodSpecification: newSpecificationId
						? newSpecificationId
						: projectTemp?.specification || null,
					name: building.name,
					additionalInfo: "",
					levels: isNaviate ? levels : null,
					levelFrom,
					levelTo,
					defaultLevelHeight,
				});
			});

			//Export versions
			const tempExports = projectTemp?.exports;
			const pricePerExport = priceJS
				? priceJS.exportsPrice?.pricePerExport
				: null;

			let exports = [];
			tempExports.forEach((item) => {
				exports.push({
					id: item.exportId,
					price: pricePerExport ? pricePerExport?.[item.exportId] : null,
				});
			});

			// Services selected on the Services step — persisted on the order as
			// manualIds (EXE-274); BE merges them with the default job manuals.
			const serviceManualIds = (projectTemp?.services || []).map(
				(service) => service.id
			);

			const body = {
				name: projectTemp.name,
				size: size,
				numberOfBuildings: tempBuildings.length,
				additionalInfo: projectTemp.additionalInfo,
				deliveryFormats: [projectTemp.deliveryFormat],
				revitFileId: projectTemp?.template,
				buildings,
				exports: isNaviate ? [] : exports,
				clientCompany: customer || null,
				status: isNaviate ? "accepted" : "requested",
				price: priceJS,
				// Snapshot of the "Input requirements" legal document shown on this
				// step — stored as-is on the order (unlike termsAndConditions, which
				// the BE fills server-side from the default terms).
				inputRequirement: inputRequirementsDetails || null,
				...(serviceManualIds.length > 0 ? { manualIds: serviceManualIds } : {}),
			};

			createOrder(body, isNaviate, (offer, cb) => {
				if (isNaviate) {
					const building = offer.buildings[0];

					uploadFiles(
						building?.id,
						projectTemp.disciplineLevels,
						projectTemp.tempFiles,
						(filesData, deliveredToBuildingFileIds) => {
							updateJobFile(
								building?.jobs[0]?.id,
								building?.levels,
								filesData,
								deliveredToBuildingFileIds,
								cb
							);
						}
					);
				} else {
					navigate("/offers");
				}
			}).then(() => navigate("/offers"));
		},
		[
			price,
			isNaviate,
			customer,
			inputRequirementsDetails,
			createOrder,
			tempBuilding,
			uploadFiles,
			updateJobFile,
			navigate,
		]
	);

	const onSubmit = () => {
		try {
			if (isValid) {
				setLoading(true);
				const projectTemp = props.projectTemp?.toJS();

				const isDraft =
					`${projectTemp.configurationSpecification}-draft` ===
					projectTemp.specification;

				if (isDraft) {
					const disciplineComponentCategoryLods = [];

					projectTemp.buildingDisciplineComponentCategoryLods.forEach(
						(disciplineLod) => {
							disciplineComponentCategoryLods.push({
								discipline: disciplineLod.discipline?.id || null,
								componentCategory: disciplineLod.componentCategory?.id || null,
								lod: disciplineLod.lod?.id || null,
							});
						}
					);

					const body = {
						name: projectTemp.name,
						company: customer,
						disciplineComponentCategoryLods:
							disciplineComponentCategoryLods || [],
					};

					createLodSpecification(
						body,
						(result) => {
							// Only proceed if LOD specification was created successfully
							if (result) {
								createOrderAction(projectTemp, result.id);
							}
							// On error, result will be null and order won't be created
						},
						true
					);
				} else {
					createOrderAction(projectTemp);
				}
			}
		} catch (error) {
			console.log({ error });
			return;
		}
	};

	// Shared by both footer layouts — rendered bare for Naviate (byte-identical
	// to the pre-grand-total footer) and inside the grand-total row otherwise.
	const stepNavigation = (
		<CustomStepNavigation
			showCancel={true}
			cancelButtonAction={() => cancelButtonAction()}
			leftButtonLabel={localize(LocKeys.BACK)}
			rightButtonClasses="btn--success"
			rightButtonLabel={localize(LocKeys.SEND_REQUEST)}
			leftButtonAction={() => props.previousPage()}
			rightButtonLoading={loading}
			rightButtonDisabled={!isValid || loading}
		/>
	);

	return (
		<>
			<form
				className={`form d-flex flex-column flex-auto standardized-form	`}
				onSubmit={props.handleSubmit(onSubmit)}
				autoComplete="off"
				noValidate
			>
				<HeaderLayout wrapperClasses="px-24 d-flex align-items-center">
					<h3>
						{localize(
							isBimifyUser
								? LocKeys.ADDITIONAL_INFORMATION
								: LocKeys.INPUT_REQUIREMENTS
						)}
					</h3>
				</HeaderLayout>
				<BodyLayout wrapperClasses="p-24">
					{/* "Input requirements" — customer/reseller-facing only (Bimify
					    users creating an order don't see it); hidden while no
					    document exists. */}
					{!isBimifyUser && !!inputRequirementsDetails && (
						<div className="row">
							<div className="col col-100 flex-column mb-24">
								<TermsEditor
									details={inputRequirementsDetails}
									isInputRequirements={true}
									editView={false}
								/>
							</div>
						</div>
					)}

					{/* Additional info is Bimify-internal (review 2026-06-09 §6):
					    customers and resellers can no longer see or edit it. */}
					{isBimifyUser && (
						<div className="row">
							<div className="col col-100 flex-column">
								<div className="card">
									<AdditionalInfoEditor
										scrollerClasses="Lexical-content--515"
										isEdit={true}
										additionalInfo={additionalInfo}
										updateEditorState={setNewDescriptionValue}
										editView={true}
										placeholder={`${localize(
											LocKeys.ADDITIONAL_INFORMATION_PLACEHOLDER
										)}...`}
									/>
								</div>
							</div>
						</div>
					)}
				</BodyLayout>

				<FooterLayout wrapperClasses="px-24">
					{isNaviate ? (
						stepNavigation
					) : (
						<div className="row row--6p-gutters d-flex align-items-center">
							{/* Running grand total — every wizard step shows the sum of
							    everything selected so far (review 2026-06-09, §5) in the
							    footer, next to the navigation buttons. */}
							<div className="col col-25">
								<OrderTotal
									variant="footer"
									label={localize(LocKeys.GRAND_TOTAL)}
									totalPrice={totalOfferPrice}
									shouldEstimatePrice={shouldEstimatePrice}
								/>
							</div>

							<div className="col col-75 color-n500">{stepNavigation}</div>
						</div>
					)}
				</FooterLayout>
			</form>
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		additionalInfo: state.getIn(["projectTemp", "additionalInfo"]) || "",
		orderId: state.getIn(["projectTemp", "project", "id"]),
		projectTemp: state.getIn(["projectTemp"]),
		customer: state.getIn(["global", "customer"]),
		tempBuilding: state.getIn(["building", "tempBuilding"]),
		price: state.get("price"),
		inputRequirementsDetails: state.getIn([
			"administration",
			"inputRequirements",
			"details",
		]),
		totalOfferPrice: state.getIn(["price", "totalOfferPrice"]),
	};
};

export default connect(mapStateToProps, {
	setTempAdditionalInfo,
	createOrder,
	createLodSpecification,
	uploadToDigitalOcean,
	getPresignedPostUploadUrl,
	getInputRequirements,
	updateJob,
})(
	reduxForm({
		form: "createOrderForm",
		touchOnBlur: false,
		destroyOnUnmount: false,
		enableReinitialize: false,
	})(AdditionalInformation)
);
